import type { EvidenceAsset, EvidenceUpload } from '../../../core/evidence';

export const MAX_EVIDENCE_BYTES = 200 * 1024 * 1024;
export const MAX_EVIDENCE_FILES = 10;
export const EVIDENCE_ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf,.jpg,.jpeg,.png,.webp,.pdf';
const mimeTypes = new Set(['image/jpeg', 'image/png', 'image/webp', 'application/pdf']);

export interface EvidenceItem {
  id: string;
  file: File;
  original: File;
  fields: EvidenceUpload;
  previewUrl: string | null;
  rotation: number;
  attempted: boolean;
  status: 'ready' | 'rotating' | 'uploading' | 'uploaded' | 'error';
  error?: string;
  asset?: EvidenceAsset;
}

export function evidenceFileType(file: Pick<File, 'name' | 'type'>): string {
  if (file.type) return file.type.toLowerCase();
  const extension = file.name.split('.').pop()?.toLowerCase();
  const extensions: Record<string, string> = {
    jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', pdf: 'application/pdf',
  };
  return extensions[extension || ''] || '';
}

export function evidenceFileError(file: Pick<File, 'name' | 'type' | 'size'>): string | null {
  if (file.size < 1 || file.size > MAX_EVIDENCE_BYTES) return 'native.capture.fileSize';
  return mimeTypes.has(evidenceFileType(file)) ? null : 'native.capture.fileType';
}

/** Reordering does not change the upload identity, bytes, or source envelope. */
export function moveEvidenceItem(items: EvidenceItem[], id: string, offset: -1 | 1): EvidenceItem[] {
  const index = items.findIndex((item) => item.id === id);
  const target = index + offset;
  if (index < 0 || target < 0 || target >= items.length) return items;
  const moved = [...items];
  [moved[index], moved[target]] = [moved[target]!, moved[index]!];
  return moved;
}

/** Rotate once from the original image; cache the resulting File for every retry. */
export async function rotateEvidenceFile(file: File, degrees: number, signal?: AbortSignal): Promise<File> {
  if (degrees % 360 === 0) return file;
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      const abort = () => { image.src = ''; reject(new DOMException('Aborted', 'AbortError')); };
      const cleanup = () => signal?.removeEventListener('abort', abort);
      image.onload = () => { cleanup(); resolve(); };
      image.onerror = () => { cleanup(); reject(new Error('native.capture.imageFailed')); };
      if (signal?.aborted) { abort(); return; }
      signal?.addEventListener('abort', abort, { once: true }); image.src = url;
    });
    const { naturalWidth: width, naturalHeight: height } = image;
    if (!width || !height || width * height > 16_000_000 || width > 10_000 || height > 10_000) {
      throw new Error('native.capture.imageDimensions');
    }
    const canvas = document.createElement('canvas');
    const swap = degrees % 180 !== 0;
    canvas.width = swap ? height : width; canvas.height = swap ? width : height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('native.capture.imageFailed');
    context.translate(canvas.width / 2, canvas.height / 2);
    context.rotate(degrees * Math.PI / 180);
    context.drawImage(image, -width / 2, -height / 2);
    const type = evidenceFileType(file) === 'image/jpeg' ? 'image/jpeg' : 'image/png';
    const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob((value) => value
      ? resolve(value) : reject(new Error('native.capture.imageFailed')), type, 0.94));
    // Release canvas backing memory before returning the cached upload bytes.
    canvas.width = 0; canvas.height = 0;
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    if (blob.size > MAX_EVIDENCE_BYTES) throw new Error('native.capture.fileSize');
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + (type === 'image/jpeg' ? '.jpg' : '.png'), {
      type, lastModified: file.lastModified,
    });
  } finally { URL.revokeObjectURL(url); }
}
